// Turun TEK sunum yüzeyi — kabuk turu da ekran turu da bunu kullanır.
//
// Modal DEĞİL, bilinçli olarak. Yerine geçtiği WelcomePanel house <Modal>'ını
// kullanıyordu: scrim + focus trap + body scroll lock. Yani tur açıkken ürün
// gerçekten görünmez ve tıklanamazdı — tanıtması gereken şeyi gizliyordu, ve
// bunu 37 adım boyunca yapıyordu. Popover spec-45'in cmp_ds_popover
// sözleşmesini taşıyor: focus trap yok, scroll lock yok, Escape ile kapanır.
//
// closeOnOutsideClick KAPALI: sayfaya tıklamak turu öldürmemeli. Kullanıcının
// çerçevelenen sekmeye basıp turun onu takip etmesini beklemesi meşrudur — ve
// bu tam olarak modalın yasakladığı, bu turun izin verdiği şey.

import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Button, Popover } from "@novemai/components";

import { useAnchorRect } from "./use-anchor-rect";

const BOX_WIDTH = 340;
/** Kutunun yüksekliği içeriğe göre değişir; taşma kararı için üst sınır. */
const BOX_MAX_HEIGHT = 220;
const GAP = 14;

/**
 * Kutuyu hedefin yanına koy — altına, üstüne, ya da YANINA.
 *
 * "Altına sığmıyorsa üstüne" TAM YÜKSEKLİKTEKİ bir eleman için ikisini de
 * yapamaz ve kutuyu 14px'e düşürür: sol ray tam da öyle bir eleman ve kutu
 * tanıtmaya çalıştığı rayın üstünü kapatıyordu (tarayıcıda ölçüldü — hiçbir
 * birim testi bunu gösteremezdi, çünkü ayrı ayrı her sayı doğruydu). Uzun
 * elemanın cevabı dikey değil YATAY.
 *
 * Yana konan kutu hedefin yanındaki İÇERİĞİN üstüne biner ve bunu engelleyecek
 * boş bir yer yok (E20: kutu sayfa başlığını örtüyordu). Seçilebilen tek şey
 * NEREYİ örteceği: dikey ortalama başlığı ve ilk kartları örtüyordu, alta
 * hizalama sayfanın en az taşıdığı köşeyi. Bu yüzden `top` değil `bottom`:
 * kutunun yüksekliği içeriğe göre değişir ve alt kenarı hedefin alt kenarına
 * ancak böyle yapışır.
 *
 * Sıra: alt → üst → sağ → sol. Her aday viewport'a clamp'lenir, yani en kötü
 * hâlde kutu ekranda kalır.
 */
type BoxPlacement =
  | { readonly top: number; readonly left: number }
  | { readonly bottom: number; readonly left: number };

function placeBox(rect: DOMRect): BoxPlacement {
  const clampLeft = (x: number): number =>
    Math.min(Math.max(GAP, x), Math.max(GAP, window.innerWidth - BOX_WIDTH - GAP));

  if (rect.bottom + GAP + BOX_MAX_HEIGHT <= window.innerHeight) {
    return { top: rect.bottom + GAP, left: clampLeft(rect.left) };
  }
  if (rect.top - GAP - BOX_MAX_HEIGHT >= 0) {
    return { top: rect.top - GAP - BOX_MAX_HEIGHT, left: clampLeft(rect.left) };
  }
  // Dikey sığmıyor: hedef ekran kadar uzun. Yanına koy, alt kenarını hedefin
  // GÖRÜNEN alt kenarına hizala.
  const bottom = window.innerHeight - Math.min(rect.bottom, window.innerHeight) + GAP;
  if (rect.right + GAP + BOX_WIDTH <= window.innerWidth) {
    return { bottom, left: rect.right + GAP };
  }
  return { bottom, left: clampLeft(rect.left - GAP - BOX_WIDTH) };
}

/**
 * Halkanın ÇİZİLECEĞİ dikdörtgen — ölçülenin viewport'la kesişimi.
 *
 * ÖLÇÜLEN KUSUR. Ekrandan uzun bir çapada (uzun liste, geniş tablo) halka
 * hedefin gerçek kutusuna çiziliyordu: üstü de altı de ekranın dışında kalıyor
 * ve kullanıcı vurgu yerine tek bir dikey çizgi görüyordu — yani "hiçbir şey
 * işaretlenmemiş". /settings/approval-chains'te 13483px'lik liste üzerinde
 * ekran görüntüsüyle doğrulandı; aynı şey /people tablosunda da olur.
 *
 * Kırpma bilinçli olarak DÜRÜST tarafta: halka viewport kenarlarına yapışır,
 * yani "bölge burada bitiyor" demez, "şu an gördüğünüz bu" der.
 *
 * `viewportHeight <= 0` iken kırpma YOK: gizli sekme ve happy-dom sıfır
 * bildirir, ve sıfıra kırpmak halkayı yok ederdi (bkz. document.hidden dersi).
 */
export function ringRect(
  rect: DOMRect,
  viewportHeight: number,
  margin = GAP,
): { readonly top: number; readonly left: number; readonly width: number; readonly height: number } {
  const base = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
  if (viewportHeight <= 0 || rect.height <= viewportHeight) return base;
  const top = Math.max(rect.top, margin);
  const bottom = Math.min(rect.bottom, viewportHeight - margin);
  // Çapa tamamen ekranın dışındaysa (kaydırma yarışı) kesişim negatif olur;
  // negatif yükseklikli bir halka çizmek yerine ölçüleni bırak.
  if (bottom <= top) return base;
  return { top, left: rect.left, width: rect.width, height: bottom - top };
}

export interface SpotlightProps {
  readonly anchorId: string;
  readonly scope: "main" | "document";
  readonly stepIndex: number;
  readonly stepCount: number;
  readonly title: string;
  readonly body: string;
  readonly onNext: () => void;
  readonly onBack: () => void;
  /** "Atla" düğmesi — kasıtlı çıkış. */
  readonly onSkip: () => void;
  /**
   * Escape — KAZA olabilecek çıkış, ve bu yüzden onSkip'ten ayrı.
   *
   * İkisini tek geri çağrıya bağlamak cazip ve yanlış: kabuk turunda onSkip
   * onaylıyor (seen_version yazıyor), dolayısıyla Escape'i ona bağlamak turu
   * tek bir yanlış tuş vuruşuyla sonsuza dek susturur.
   */
  readonly onEscape: () => void;
}

export function Spotlight({
  anchorId,
  scope,
  stepIndex,
  stepCount,
  title,
  body,
  onNext,
  onBack,
  onSkip,
  onEscape,
}: SpotlightProps): React.ReactElement | null {
  const { t } = useTranslation();
  const rect = useAnchorRect(anchorId, scope);

  // Çözülemeyen çapa = çizilmeyen adım. Hiçbir şeyi çerçevelemeyen bir kutu,
  // kullanıcıya "şuraya bak" deyip boş bir yeri işaret eder; çağıran bu null'ı
  // görüp adımı atlar.
  if (rect === null) return null;

  const isLast = stepIndex === stepCount - 1;
  const ring = ringRect(rect, window.innerHeight);
  const placement = placeBox(rect);
  const stepLabel = t("tour.panel.step_of", { current: stepIndex + 1, total: stepCount });

  return (
    <>
      {/* Halka document.body'ye PORTAL'lanıyor, bileşen ağacında bırakılmıyor.
          position:fixed'in referansı viewport DEĞİLDİR: `transform`, `filter`
          ya da `will-change` taşıyan herhangi bir ata onu kendi kutusuna
          bağlar. Kabukta öyle bir ata var ve halka hedefin 546px sağına
          düşüyordu — BOYUTU doğru, KONUMU yanlış (tarayıcıda ölçüldü; hiçbir
          birim testi bunu gösteremez, çünkü happy-dom düzen hesaplamıyor).
          Popover zaten kendi Portal'ını kullanıyor; halka da aynı düzlemde
          olmak zorunda. */}
      {createPortal(
        <div
          data-tour-ring=""
          className="nai-tour-ring"
          style={{
            top: `${String(ring.top)}px`,
            left: `${String(ring.left)}px`,
            width: `${String(ring.width)}px`,
            height: `${String(ring.height)}px`,
          }}
        />,
        document.body,
      )}
      <Popover
        open
        onClose={onEscape}
        closeOnOutsideClick={false}
        aria-label={title}
        className="nai-tour-box"
        style={{
          ...("top" in placement
            ? { top: `${String(placement.top)}px` }
            : { bottom: `${String(placement.bottom)}px` }),
          left: `${String(placement.left)}px`,
          width: `${String(BOX_WIDTH)}px`,
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="h-1 flex-1 overflow-hidden rounded-full bg-[var(--bg-subtle)]"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={stepCount}
            aria-valuenow={stepIndex + 1}
            aria-label={stepLabel}
          >
            <div
              className="h-full rounded-full bg-[var(--accent)]"
              style={{ width: `${String(((stepIndex + 1) / stepCount) * 100)}%` }}
            />
          </div>
          <span className="shrink-0 font-sans text-xs tabular-nums text-[var(--fg-muted)]">
            {stepLabel}
          </span>
        </div>

        <h3 className="mt-3 font-serif text-[17px] font-medium text-[var(--fg-primary)]">{title}</h3>
        <p className="mt-1.5 font-sans text-sm leading-relaxed text-[var(--fg-secondary)]">{body}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="tertiary" size="sm" onClick={onSkip}>
            {t("tour.panel.skip")}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={stepIndex === 0} onClick={onBack}>
              {t("tour.panel.back")}
            </Button>
            <Button variant="primary" size="sm" onClick={onNext}>
              {isLast ? t("tour.panel.finish") : t("tour.panel.next")}
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
